/**
 * Hoja 06 del paquete: Estado de Cambios en el Patrimonio, conceptos × componentes.
 *
 * Las columnas SON los renglones del patrimonio del Estado de Situación, así
 * que el saldo final de la matriz es el mismo patrimonio que se presenta dos
 * hojas antes. Las dos no pueden contradecirse.
 *
 * El detalle por cuenta es una SUB-PESTAÑA. Como entrada del menú se llamaba
 * «Estado de Cambios en el Patrimonio» sin ser la matriz, y desde el menú no
 * había forma de distinguir el estado de su detalle.
 */

import { Outlet } from "react-router-dom";
import { StatusBadge } from "../../components/StatusBadge";
import { Tabs } from "../../components/Tabs";
import { formatMoney, moneyClass } from "../../components/format";
import { isBalanced, useClosing } from "./closingContext";
import { ReportPanel } from "./ReportPanel";
import { StatementHead } from "./shared";

const SUB_PESTANAS = [
  { to: "/closing/equity", label: "Estado oficial", end: true },
  { to: "/closing/equity/detail", label: "Detalle por cuenta" },
];

/** La hoja: encabezado, sub-pestañas y la vista elegida. */
export function EquityMatrix() {
  const contexto = useClosing();
  const { package: pkg } = contexto;

  return (
    <>
      <StatementHead
        title="Estado de Cambios en el Patrimonio"
        subtitle={`movimiento del ${pkg.period_from} al ${pkg.period_to}`}
        homologation={pkg.homologation.equity_statement}
      />

      <Tabs
        tabs={SUB_PESTANAS}
        variant="inline"
        label="Vistas del Estado de Cambios en el Patrimonio"
      />

      <Outlet context={contexto} />
    </>
  );
}

/** Detalle por cuenta del patrimonio, dentro de la hoja que lo presenta. */
export function EquityDetail() {
  return (
    <ReportPanel
      reportKey="equity_changes"
      /* El texto NO promete composición. Las dos vistas clasifican por
         criterios distintos y no tienen por qué coincidir: la matriz sale de
         los renglones del ESF —su columna RESULTADO viene de las cuentas de
         clase 4 y 5 vía `B31050`—, y este detalle clasifica los prefijos 3101
         a 3106 del plan. Decir que responde «de qué cuenta compone cada
         columna» invitaba a restar dos cifras que miden cosas distintas y a
         leer un descuadre donde no lo hay. */
      explanation="Las cuentas patrimoniales del plan (3101 a 3106) con su
        movimiento del período. NO es el desglose de la matriz: la matriz sale
        de los renglones del Estado de Situación —su columna de resultado viene
        de las cuentas de resultado— y esta vista, del plan de cuentas. Las dos
        cifras pueden diferir sin que ninguna esté mal."
    />
  );
}

/** La matriz tal como se firma. */
export function EquityOfficial() {
  const { package: pkg } = useClosing();
  const matriz = pkg.equity;
  const cierre = pkg.checks.find((check) => check.id === "ecp_cierra");

  return (
    <>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Conceptos</th>
                {matriz.columns.map((column) => (
                  <th key={column.id} scope="col" className="num">
                    {column.label}
                  </th>
                ))}
                <th scope="col" className="num">
                  Total patrimonio
                </th>
              </tr>
            </thead>
            <tbody>
              {matriz.rows.map((row) => (
                <tr
                  key={row.id}
                  className={row.kind === "movement" ? undefined : "level-1"}
                >
                  <td>{row.label}</td>
                  {matriz.columns.map((column) => (
                    <td key={column.id} className={moneyClass(row.amounts[column.id])}>
                      {formatMoney(row.amounts[column.id])}
                    </td>
                  ))}
                  <td className={moneyClass(row.total)}>{formatMoney(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card banner">
        <StatusBadge tone={isBalanced(cierre?.difference ?? null) ? "good" : "critical"}>
          {isBalanced(cierre?.difference ?? null) ? "Cuadra" : "Descuadre"}
        </StatusBadge>
        <div className="banner-body">
          <strong>
            Saldo final {formatMoney(matriz.closing_total)} contra el patrimonio del
            Estado de Situación
          </strong>
          <p className="small muted">
            Diferencia {formatMoney(cierre?.difference ?? null)}.
          </p>
        </div>
      </div>
    </>
  );
}
